import { Search, User } from 'lucide-react';
import { useState } from 'react';
import { useTrackCrmSearch } from '../hooks/useTrackCrmSearch';
import { useHubSpotContacts } from '../services/dealCreation.api';
import type { DealCreationDraft, RecordMode } from '../utils/dealCreationDraft';
import { CrmNoMatch, CrmRecordRow, CrmSearchField, SelectedRecord } from './CrmRecordPicker';
import { DealField } from './DealField';
import { RecordModeToggle } from './RecordModeToggle';

interface ContactStepProps {
  draft: DealCreationDraft;
  onChange: (patch: Partial<DealCreationDraft>) => void;
}

const CONTACT_ICON = <User aria-hidden />;

/** The free-text index does not answer usefully on one or two letters. */
const MIN_SEARCH_LENGTH = 3;

/**
 * Step 2: the person to talk to. One field takes a name or an email, because the BD rarely knows
 * the address by heart, and the matches are listed rather than resolved: several people can carry
 * the same name, so the BD picks.
 */
export function ContactStep({ draft, onChange }: ContactStepProps) {
  const [term, setTerm] = useState('');
  const [searched, setSearched] = useState('');
  const { data, isFetching, isError } = useHubSpotContacts(
    searched,
    draft.contactMode === 'attach',
  );
  const contacts = data?.contacts ?? [];
  // The attached contact comes from the draft so it survives a remount; the live query only backs
  // the picker below.
  const attached = draft.contactId !== '' ? draft.attachedContact : undefined;
  // A read that failed is not a portal without that contact: telling them apart is what keeps the
  // BD from creating a duplicate on the back of a 500 or an expired session.
  const noMatch = !isFetching && !isError && searched !== '' && contacts.length === 0;
  useTrackCrmSearch({
    recordType: 'contact',
    searched,
    isFetching,
    resultCount: contacts.length,
  });
  const fullName = (first?: string, last?: string) => [first, last].filter(Boolean).join(' ');

  // Switching to the create form carries over a search that matched nothing, but only when it was
  // an address: a surname in the email field would ship a contact whose email is a name.
  const switchMode = (contactMode: RecordMode) => {
    const carriesTheEmail =
      contactMode === 'create' &&
      noMatch &&
      searched.includes('@') &&
      draft.contact.email.trim() === '';
    if (carriesTheEmail) {
      onChange({ contactMode, contact: { ...draft.contact, email: searched } });
      return;
    }
    onChange({ contactMode });
  };

  return (
    <div className="flex flex-col gap-4">
      <RecordModeToggle mode={draft.contactMode} onChange={switchMode} />

      {draft.contactMode === 'attach' ? (
        attached ? (
          <SelectedRecord
            icon={CONTACT_ICON}
            title={fullName(attached.first_name, attached.last_name) || attached.email}
            subtitle={[attached.email, attached.job_title].filter(Boolean).join(' · ')}
            clearLabel="Change contact"
            onClear={() => onChange({ contactId: '', attachedContact: undefined })}
          />
        ) : (
          <div className="flex flex-col gap-2.5">
            <CrmSearchField
              id="contact-search"
              label="Name or email"
              icon={<Search aria-hidden />}
              placeholder="First name, last name or email"
              value={term}
              minSearchLength={MIN_SEARCH_LENGTH}
              onChange={setTerm}
              onSearch={() => setSearched(term.trim())}
            />

            {isFetching && <p className="text-sm text-primary-500">Searching the CRM…</p>}

            {isError && (
              <CrmNoMatch>The CRM could not be reached — try the search again.</CrmNoMatch>
            )}

            {noMatch && <CrmNoMatch>No match — try “Create new”.</CrmNoMatch>}

            <div className="flex max-h-[220px] flex-col gap-0.5 overflow-y-auto">
              {!isFetching &&
                contacts.map((contact) => (
                  <CrmRecordRow
                    key={contact.id}
                    icon={CONTACT_ICON}
                    title={fullName(contact.first_name, contact.last_name) || contact.email}
                    subtitle={[contact.email, contact.job_title].filter(Boolean).join(' · ')}
                    onSelect={() => onChange({ contactId: contact.id, attachedContact: contact })}
                  />
                ))}
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DealField
              id="contact-first-name"
              label="First name"
              value={draft.contact.first_name ?? ''}
              onChange={(first_name) => onChange({ contact: { ...draft.contact, first_name } })}
            />
            <DealField
              id="contact-last-name"
              label="Last name"
              value={draft.contact.last_name ?? ''}
              onChange={(last_name) => onChange({ contact: { ...draft.contact, last_name } })}
            />
          </div>
          <DealField
            id="contact-email"
            label="Email"
            type="email"
            required
            value={draft.contact.email}
            onChange={(email) => onChange({ contact: { ...draft.contact, email } })}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DealField
              id="contact-phone"
              label="Mobile phone"
              value={draft.contact.phone ?? ''}
              onChange={(phone) => onChange({ contact: { ...draft.contact, phone } })}
            />
            <DealField
              id="contact-job-title"
              label="Job title"
              value={draft.contact.job_title ?? ''}
              onChange={(job_title) => onChange({ contact: { ...draft.contact, job_title } })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
